<template>
  <div class="file-select-step">
    <Loader
      v-if="loading"
      style="z-index: 55000;"
    />

    <div class="step-content">
      <!-- Info Block - Contextual based on import type -->
      <SpmMessage
        severity="info"
        class="mb-2"
      >
        <div class="info-message-content">
          <strong>{{ t(`import.selectStep.info.${importType}.title`) }}</strong>
          <p class="info-description">
            {{ t(`import.selectStep.info.${importType}.description`) }}
          </p>
          <div
            v-if="importType === 'contact'"
            class="identifier-hints mt-2"
          >
            <div class="hint-item">
              <span class="hint-badge hint-badge-primary">{{ t('import.mapping.fields.label.email') }}</span>
              <span class="hint-or">{{ t('import.selectStep.or') }}</span>
              <span class="hint-badge hint-badge-primary">{{ t('import.mapping.fields.label.phone') }}</span>
              <span class="hint-or">{{ t('import.selectStep.or') }}</span>
              <span class="hint-badge hint-badge-secondary">{{ t('import.selectStep.pushIdentifier') }}</span>
            </div>
          </div>
        </div>
      </SpmMessage>

      <!-- Resources Panel (optional) -->
      <div
        v-if="showResources"
        class="resources-panel mb-4"
      >
        <h4 class="resources-title">
          <i class="fas fa-book-open mr-2" />
          {{ t('import.selectStep.resources.title') }}
        </h4>
        <div class="resources-links">
          <a
            href="#"
            class="resource-link"
            @click.prevent="$emit('open-help', 'format')"
          >
            <i class="fas fa-external-link-alt" />
            {{ t('import.selectStep.resources.formatHelp') }}
          </a>
          <a
            href="#"
            class="resource-link"
            @click.prevent="$emit('open-help', 'fields')"
          >
            <i class="fas fa-external-link-alt" />
            {{ t('import.selectStep.resources.fieldsHelp') }}
          </a>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="main-content-area">
        <div class="input-section">
          <!-- File Upload Mode -->
          <div
            v-if="!useCopyPaste"
            class="file-upload-section"
          >
            <div
              class="upload-area"
              :class="{
                'drag-over': isDragOver,
                'has-file': selectedFile,
                'has-error': fileError
              }"
              @drop.prevent="onDrop"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @click="triggerFileInput"
            >
              <input
                ref="fileInput"
                type="file"
                :accept="accept"
                class="hidden-input"
                @change="onFileSelect"
              >

              <template v-if="!selectedFile">
                <div class="upload-icon-wrapper">
                  <i
                    class="fas fa-cloud-upload-alt upload-icon"
                    :class="{ 'drag-active': isDragOver }"
                  />
                </div>
                <h4 class="upload-title">
                  {{ isDragOver ? t('import.selectStep.upload.dropHere') : uploadTitle }}
                </h4>
                <p class="upload-subtitle">
                  {{ uploadSubtitle }}
                </p>
                <Button
                  :label="t('import.selectStep.upload.browse')"
                  icon="fas fa-folder-open"
                  class="browse-button"
                  @click.stop="triggerFileInput"
                />
                <div class="upload-meta">
                  <div class="meta-item">
                    <i class="fas fa-file-alt" />
                    <span>{{ t('import.selectStep.upload.acceptedFormats') }}: {{ acceptedFormatsDisplay }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fas fa-weight-hanging" />
                    <span>{{ t('import.selectStep.upload.maxSize') }}: {{ formatFileSize(maxFileSize) }}</span>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="selected-file-display">
                  <div class="file-info">
                    <div class="file-icon-wrapper">
                      <i :class="getFileIcon(selectedFile.name)" />
                    </div>
                    <div class="file-details">
                      <span class="file-name">{{ selectedFile.name }}</span>
                      <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                    </div>
                    <Button
                      icon="fas fa-times"
                      class="p-button-rounded p-button-text p-button-danger remove-file-btn"
                      @click.stop="removeFile"
                    />
                  </div>
                  <div class="file-ready-message">
                    <i class="fas fa-check-circle" />
                    <span>{{ t('import.selectStep.upload.fileReady') }}</span>
                  </div>
                </div>
              </template>
            </div>

            <div
              v-if="fileError"
              class="error-message"
            >
              <i class="fas fa-exclamation-triangle" />
              <span>{{ fileError }}</span>
            </div>
          </div>

          <!-- Copy-Paste Mode -->
          <div
            v-else
            class="copy-paste-section"
          >
            <div class="paste-header">
              <h4 class="paste-title">
                <i class="fas fa-paste mr-2" />
                {{ t('import.selectStep.copyPaste.title') }}
              </h4>
              <span
                v-if="pastedText"
                class="line-count"
              >
                {{ getLineCount() }} {{ t('import.selectStep.copyPaste.lineCount') }}
              </span>
            </div>
            <Textarea
              ref="textareaRef"
              v-model="pastedText"
              :auto-resize="false"
              rows="12"
              class="paste-textarea"
              :class="{ 'p-invalid': pasteError }"
              :placeholder="t('import.selectStep.copyPaste.placeholder')"
              @input="validatePastedText"
            />
            <div
              v-if="pasteError"
              class="error-message"
            >
              <i class="fas fa-exclamation-triangle" />
              <span>{{ pasteError }}</span>
            </div>
            <p class="paste-hint">
              <i class="fas fa-lightbulb" />
              {{ t('import.selectStep.copyPaste.hint') }}
            </p>
          </div>
        </div>

        <!-- Toggle Link (only if copy-paste is allowed) -->
        <div
          v-if="allowCopyPaste"
          class="toggle-mode-link"
        >
          <a
            href="#"
            @click.prevent="toggleMode"
          >
            <i :class="useCopyPaste ? 'fas fa-file-upload' : 'fas fa-paste'" />
            {{ useCopyPaste
              ? t('import.selectStep.switchToFile')
              : t('import.selectStep.switchToCopyPaste')
            }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Loader from '@/components/layout/Loader.vue';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';

export default defineComponent({
  name: 'FileSelectStep',

  components: {
    Button,
    Textarea,
    Loader,
    SpmMessage,
  },

  props: {
    importType: {
      type: String,
      required: true,
    },

    accept: {
      type: String,
      default: '.csv,.xlsx,.txt',
    },

    maxFileSize: {
      type: Number,
      default: 70000000, // 70MB
    },

    showResources: {
      type: Boolean,
      default: true,
    },

    modelValue: {
      type: Object as PropType<{ file: File | null; pastedText: string; useCopyPaste: boolean }>,
      default: () => ({ file: null, pastedText: '', useCopyPaste: false }),
    },

    /** Whether copy-paste mode is allowed */
    allowCopyPaste: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue', 'valid-change', 'open-help'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const loading = ref(false);
    const fileInput = ref<HTMLInputElement | null>(null);
    const textareaRef = ref<InstanceType<typeof Textarea> | null>(null);
    const isDragOver = ref(false);
    const selectedFile = ref<File | null>(props.modelValue?.file || null);
    const pastedText = ref(props.modelValue?.pastedText || '');
    const useCopyPaste = ref(props.modelValue?.useCopyPaste || false);
    const fileError = ref<string | null>(null);
    const pasteError = ref<string | null>(null);

    const isValid = computed(() => {
      if (useCopyPaste.value) {
        return !!pastedText.value.trim() && !pasteError.value;
      }
      return !!selectedFile.value && !fileError.value;
    });

    // Display accepted formats based on accept prop
    const acceptedFormatsDisplay = computed(() => {
      const formats = props.accept
        .split(',')
        .map((ext) => ext.replace('.', '').toUpperCase().trim());
      return formats.join(', ');
    });

    // Check if this is a zip-only upload
    const isZipOnly = computed(() => props.accept.trim() === '.zip');

    // Dynamic upload title based on accepted formats
    const uploadTitle = computed(() => {
      if (isZipOnly.value) {
        return t('import.selectStep.upload.titleZip');
      }
      return t('import.selectStep.upload.title');
    });

    // Dynamic upload subtitle based on accepted formats
    const uploadSubtitle = computed(() => {
      if (isZipOnly.value) {
        return t('import.selectStep.upload.subtitleZip');
      }
      return t('import.selectStep.upload.subtitle');
    });

    // Emit validity changes
    watch(isValid, (newVal) => {
      emit('valid-change', newVal);
    }, { immediate: true });

    // Emit model value changes
    watch([selectedFile, pastedText, useCopyPaste], () => {
      emit('update:modelValue', {
        file: selectedFile.value,
        pastedText: pastedText.value,
        useCopyPaste: useCopyPaste.value,
      });
    }, { deep: true });

    // Sync with modelValue prop
    watch(() => props.modelValue, (newVal) => {
      if (newVal) {
        if (newVal.file !== selectedFile.value) {
          selectedFile.value = newVal.file;
        }
        if (newVal.pastedText !== pastedText.value) {
          pastedText.value = newVal.pastedText;
        }
        if (newVal.useCopyPaste !== useCopyPaste.value) {
          useCopyPaste.value = newVal.useCopyPaste;
        }
      }
    }, { deep: true });

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const getFileIcon = (fileName: string): string => {
      const extension = fileName.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'csv':
        case 'txt':
          return 'fas fa-file-csv file-type-icon csv';
        case 'xlsx':
        case 'xls':
          return 'fas fa-file-excel file-type-icon excel';
        default:
          return 'fas fa-file file-type-icon';
      }
    };

    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const validateFile = (file: File): boolean => {
      fileError.value = null;

      if (file.size > props.maxFileSize) {
        fileError.value = t('import.selectStep.errors.fileTooBig', {
          max: formatFileSize(props.maxFileSize),
        });
        return false;
      }

      const acceptedExtensions = props.accept.split(',').map((ext) => ext.trim().toLowerCase());
      const fileName = file.name.toLowerCase();
      const isValidType = acceptedExtensions.some((ext) => fileName.endsWith(ext.replace('.', '')));

      if (!isValidType) {
        fileError.value = t('import.selectStep.errors.invalidType', {
          types: props.accept,
        });
        return false;
      }

      return true;
    };

    const processFile = (file: File) => {
      if (validateFile(file)) {
        selectedFile.value = file;
      }
    };

    const onFileSelect = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        processFile(file);
      }
      target.value = '';
    };

    const onDrop = (event: DragEvent) => {
      isDragOver.value = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        processFile(file);
      }
    };

    const onDragOver = () => {
      isDragOver.value = true;
    };

    const onDragLeave = () => {
      isDragOver.value = false;
    };

    const removeFile = () => {
      selectedFile.value = null;
      fileError.value = null;
    };

    const validatePastedText = () => {
      pasteError.value = null;

      if (!pastedText.value.trim()) {
        return;
      }

      const lines = pastedText.value.split('\n').filter((line) => line.trim());

      // Must have at least 2 lines (header + data)
      if (lines.length < 2) {
        pasteError.value = t('import.selectStep.errors.minimumTwoLines');
        return;
      }

      const hasSeparator = /[,;\t]/.test(pastedText.value);
      if (!hasSeparator) {
        pasteError.value = t('import.selectStep.errors.invalidFormat');
      }
    };

    const getLineCount = (): number => {
      if (!pastedText.value) return 0;
      return pastedText.value.split('\n').filter((line) => line.trim()).length;
    };

    const toggleMode = () => {
      useCopyPaste.value = !useCopyPaste.value;
      // Reset errors when switching
      fileError.value = null;
      pasteError.value = null;
    };

    return {
      t,
      loading,
      fileInput,
      textareaRef,
      isDragOver,
      selectedFile,
      pastedText,
      useCopyPaste,
      fileError,
      pasteError,
      acceptedFormatsDisplay,
      uploadTitle,
      uploadSubtitle,
      formatFileSize,
      getFileIcon,
      triggerFileInput,
      onFileSelect,
      onDrop,
      onDragOver,
      onDragLeave,
      removeFile,
      validatePastedText,
      getLineCount,
      toggleMode,
    };
  },
});
</script>

<style scoped lang="scss">
.file-select-step {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

// Info Block
.info-block {
  background: linear-gradient(135deg, var(--blue-50) 0%, var(--surface-0) 100%);
  border: 1px solid var(--blue-200);
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid var(--blue-500);
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.info-icon {
  font-size: 1.5rem;
  color: var(--blue-500);
}

.info-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--blue-800);
}

.info-message-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-description {
  margin: 0;
  color: inherit;
  line-height: 1.5;
  font-size: 0.875rem;
  opacity: 0.9;
}

.identifier-hints {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.hint-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.hint-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;

  &.hint-badge-primary {
    background-color: var(--primary-100);
    color: var(--primary-700);
    border: 1px solid var(--primary-200);
  }

  &.hint-badge-secondary {
    background-color: var(--surface-200);
    color: var(--text-color-secondary);
    border: 1px solid var(--surface-300);
  }
}

.hint-or {
  color: var(--text-color-secondary);
  font-size: 0.85rem;
}

// Resources Panel
.resources-panel {
  background-color: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.resources-title {
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color);
}

.resources-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.resource-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }

  i {
    font-size: 0.75rem;
  }
}

// Main Content Area
.main-content-area {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.input-section {
  min-height: 350px;
}

// File Upload Section
.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  border: 2px dashed var(--surface-300);
  border-radius: 12px;
  background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-0) 100%);
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 320px;

  &:hover {
    border-color: var(--primary-400);
    background: linear-gradient(135deg, var(--primary-50) 0%, var(--surface-0) 100%);
  }

  &.drag-over {
    border-color: var(--primary-500);
    background: linear-gradient(135deg, var(--primary-100) 0%, var(--primary-50) 100%);
    transform: scale(1.01);

    .upload-icon {
      transform: translateY(-8px);
      color: var(--primary-500);
    }
  }

  &.has-file {
    border-color: var(--green-400);
    background: linear-gradient(135deg, var(--green-50) 0%, var(--surface-0) 100%);
    cursor: default;
  }

  &.has-error {
    border-color: var(--red-400);
    background: linear-gradient(135deg, var(--red-50) 0%, var(--surface-0) 100%);
  }
}

.hidden-input {
  display: none;
}

.upload-icon-wrapper {
  margin-bottom: 1.5rem;
}

.upload-icon {
  font-size: 4rem;
  color: var(--surface-400);
  transition: all 0.3s ease;

  &.drag-active {
    color: var(--primary-500);
    transform: translateY(-8px);
  }
}

.upload-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  text-align: center;
}

.upload-subtitle {
  margin: 0 0 1.5rem 0;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
  text-align: center;
}

.browse-button {
  margin-bottom: 1.5rem;
}

.upload-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-color-secondary);

  i {
    width: 16px;
    text-align: center;
  }
}

// Selected File Display
.selected-file-display {
  width: 100%;
  max-width: 450px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: var(--surface-0);
  border: 1px solid var(--green-200);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.file-icon-wrapper {
  .file-type-icon {
    font-size: 2.5rem;

    &.csv {
      color: var(--green-600);
    }

    &.excel {
      color: var(--green-700);
    }
  }
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-name {
  font-weight: 600;
  color: var(--text-color);
  word-break: break-all;
}

.file-size {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}

.remove-file-btn {
  flex-shrink: 0;
}

.file-ready-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: var(--green-50);
  border: 1px solid var(--green-200);
  border-radius: 8px;
  color: var(--green-700);
  font-size: 0.9rem;

  i {
    color: var(--green-600);
  }
}

// Copy-Paste Section
.copy-paste-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.paste-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.paste-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.line-count {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
  background-color: var(--surface-100);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
}

.paste-textarea {
  width: 100%;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  min-height: 280px;
  resize: vertical;
}

.paste-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-color-secondary);

  i {
    color: var(--yellow-600);
  }
}

// Error Message
.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 8px;
  color: var(--red-700);
  font-size: 0.9rem;
  margin-top: 0.75rem;

  i {
    color: var(--red-600);
  }
}

// Toggle Mode Link
.toggle-mode-link {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--primary-color);
    font-size: 0.9rem;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: var(--primary-700);
      text-decoration: underline;
    }

    i {
      font-size: 0.85rem;
    }
  }
}

// Responsive
@media (max-width: 768px) {
  .file-select-step {
    padding: 1rem;
  }

  .upload-area {
    padding: 2rem 1rem;
    min-height: 280px;
  }

  .upload-icon {
    font-size: 3rem;
  }

  .upload-title {
    font-size: 1.1rem;
  }

  .info-block {
    padding: 1rem;
  }

  .resources-links {
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style>
